import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Check, AlertTriangle, X, ShoppingCart, RefreshCw, Clock, CalendarClock, Truck, Package, Minus, Plus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import type { ReorderPreviewResponse, ReorderMatchResult } from '@shared/schema';
import { queryClient } from '@/lib/queryClient';
import DateTimeSelector from '@/components/DateTimeSelector';

interface ClosedRestaurantInfo {
  hoursJson: any;
  timezone: string | null;
  eatzyClientId: string;
  restaurantName: string;
  serviceType: string;
}

class RestaurantClosedError extends Error {
  closedInfo: ClosedRestaurantInfo;
  constructor(message: string, closedInfo: ClosedRestaurantInfo) {
    super(message);
    this.closedInfo = closedInfo;
  }
}

interface ReorderValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  restaurantId: string;
  eatzyClientId?: string;
  serviceType: 'takeout' | 'delivery';
  deliveryLat?: number;
  deliveryLng?: number;
  deliveryAddressId?: number;
  onSuccess?: (info: { eatzyClientId?: string; restaurantId?: string; serviceType: string; scheduledTime?: { startTime: number; endTime: number }; action: 'checkout' | 'modify' }) => void;
}

export function ReorderValidationModal({
  isOpen,
  onClose,
  orderId,
  restaurantId,
  eatzyClientId: propEatzyClientId,
  serviceType,
  deliveryLat,
  deliveryLng,
  deliveryAddressId,
  onSuccess
}: ReorderValidationModalProps) {
  const { toast } = useToast();
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [itemQuantities, setItemQuantities] = useState<Record<number, number>>({});
  const [scheduledTime, setScheduledTime] = useState<{ startTime: number; endTime: number } | null>(null);
  const [userDismissedScheduler, setUserDismissedScheduler] = useState(false);
  const [showTimedScheduler, setShowTimedScheduler] = useState(false);

  const buildRequestBody = useCallback(() => {
    const body: any = { serviceType };
    if (serviceType === 'delivery' && deliveryLat && deliveryLng) {
      body.lat = deliveryLat;
      body.lng = deliveryLng;
    }
    if (scheduledTime) {
      body.startTime = scheduledTime.startTime;
      body.endTime = scheduledTime.endTime;
    }
    return body;
  }, [serviceType, deliveryLat, deliveryLng, scheduledTime]);

  const { 
    data: preview, 
    isLoading, 
    isFetching,
    error: previewError,
    refetch
  } = useQuery<ReorderPreviewResponse>({
    queryKey: ['/api/orders', orderId, 'reorder-preview', serviceType, deliveryLat, deliveryLng],
    queryFn: async () => {
      const body = buildRequestBody();
      const token = window.localStorage.getItem('auth_token');
      const eatzyUserToken = window.localStorage.getItem('eatzyUserToken');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (eatzyUserToken) headers['X-User-Eatzy-Token'] = eatzyUserToken;
      
      const response = await fetch(`/api/orders/${orderId}/reorder-preview`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 503 && errorData.hoursJson) {
          throw new RestaurantClosedError(
            errorData.error || 'Restaurant is currently unavailable',
            {
              hoursJson: errorData.hoursJson,
              timezone: errorData.timezone || null,
              eatzyClientId: errorData.eatzyClientId || '',
              restaurantName: errorData.restaurantName || '',
              serviceType: errorData.serviceType || serviceType,
            }
          );
        }
        throw new Error(errorData.error || 'Failed to load reorder preview');
      }
      return response.json();
    },
    enabled: isOpen && !!orderId,
    staleTime: 30000,
    gcTime: 60000,
    retry: 1,
  });

  const isClosedError = previewError instanceof RestaurantClosedError || 
    (previewError && 'closedInfo' in (previewError as any));
  const closedInfo: ClosedRestaurantInfo | null = isClosedError 
    ? (previewError as RestaurantClosedError).closedInfo 
    : null;
  const showScheduler = isClosedError && !userDismissedScheduler;

  useEffect(() => {
    if (preview) {
      const availableIndices = new Set<number>();
      const initialQuantities: Record<number, number> = {};
      preview.results.forEach((result, index) => {
        if (result.status !== 'unavailable') {
          availableIndices.add(index);
        }
        initialQuantities[index] = result.originalItem.quantity;
      });
      setSelectedItems(availableIndices);
      setItemQuantities(initialQuantities);
    }
  }, [preview]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedItems(new Set());
      setItemQuantities({});
      setScheduledTime(null);
      setUserDismissedScheduler(false);
      setShowTimedScheduler(false);
      queryClient.removeQueries({ queryKey: ['/api/orders', orderId, 'reorder-preview'] });
    }
  }, [isOpen, orderId]);

  const handleTimeSelect = useCallback((date: Date | null, isAsap: boolean) => {
    if (isAsap || !date) {
      setScheduledTime(null);
      return;
    }
    const startTimestamp = Math.floor(date.getTime() / 1000);
    const endTimestamp = startTimestamp + 900;
    setScheduledTime({ startTime: startTimestamp, endTime: endTimestamp });
  }, []);

  const handleScheduleConfirm = useCallback(() => {
    if (!scheduledTime) return;
    setUserDismissedScheduler(true);
    queryClient.removeQueries({ queryKey: ['/api/orders', orderId, 'reorder-preview'] });
    setTimeout(() => refetch(), 50);
  }, [scheduledTime, orderId, refetch]);

  const handleBackToScheduler = useCallback(() => {
    setUserDismissedScheduler(false);
    setScheduledTime(null);
    queryClient.removeQueries({ queryKey: ['/api/orders', orderId, 'reorder-preview'] });
  }, [orderId]);

  const [isProcessing, setIsProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState<'checkout' | 'modify' | null>(null);

  const handleAddToCart = (action: 'checkout' | 'modify') => {
    if (!preview) return;

    const selectedResults = preview.results
      .map((result, index) => ({ result, index }))
      .filter(({ index }) => selectedItems.has(index))
      .filter(({ result }) => result.status !== 'unavailable' && result.matchedMenuItem);

    if (selectedResults.length === 0) {
      toast({
        title: 'No items to add',
        description: 'Please select at least one available item',
        variant: 'destructive'
      });
      return;
    }

    setIsProcessing(true);
    setProcessingAction(action);

    try {
      const resolvedEatzyClientId = preview.eatzyClientId || propEatzyClientId;
      // Always key the cart by local restaurantId (UUID) so Restaurant.tsx can restore it
      // by URL param. Eatzy client ID is only used as a fallback when local ID is absent.
      const cartRestaurantId = restaurantId || resolvedEatzyClientId || preview.restaurantId;

      const cartItems = selectedResults.map(({ result, index }) => {
        const matched = result.matchedMenuItem!;
        const qty = itemQuantities[index] ?? result.originalItem.quantity;
        const modifierSelections: { [groupId: string]: string[] } = {};
        const modifierGroups: any[] = [];

        const modsToUse = (result as any).validModifiers && (result as any).validModifiers.length > 0
          ? (result as any).validModifiers
          : [];

        if (modsToUse && modsToUse.length > 0) {
          for (const mod of modsToUse) {
            const qId = String(mod.questionId).replace(/^Q/, '');
            const answerId = mod.answerId;
            if (!modifierSelections[qId]) {
              modifierSelections[qId] = [];
            }
            modifierSelections[qId].push(answerId);

            const existingGroup = modifierGroups.find((g: any) => g.id === qId);
            if (!existingGroup) {
              modifierGroups.push({
                id: qId,
                name: mod.questionName || mod.questionId || qId,
                required: false,
                multiSelect: true,
                options: [{
                  id: answerId,
                  name: mod.answerName || answerId,
                  price: mod.priceAdjustment || 0,
                }]
              });
            } else {
              if (!existingGroup.options.find((o: any) => o.id === answerId)) {
                existingGroup.options.push({
                  id: answerId,
                  name: mod.answerName || answerId,
                  price: mod.priceAdjustment || 0,
                });
              }
            }
          }
        }

        return {
          item: {
            id: matched.itemId,
            name: matched.name,
            price: matched.price,
            description: null,
            imageUrl: null,
            modifiers: modifierGroups.length > 0 ? modifierGroups : undefined,
          },
          quantity: qty,
          selections: modifierSelections,
          totalPrice: (matched.price + (result.validModifiers || []).reduce((s, m) => s + (m.priceAdjustment || 0), 0)) * qty,
          specialInstructions: result.originalItem.specialInstructions || undefined,
        };
      });

      const orderConfigForCart: any = {
        serviceType: preview.currentServiceType || serviceType,
        timing: scheduledTime ? 'scheduled' : 'asap',
      };

      if (scheduledTime) {
        const scheduledDate = new Date(scheduledTime.startTime * 1000);
        orderConfigForCart.scheduledTime = scheduledDate.toISOString();
        orderConfigForCart.scheduledStart = scheduledTime.startTime;
        orderConfigForCart.scheduledEnd = scheduledTime.endTime;
      }

      if (serviceType === 'delivery') {
        if (deliveryAddressId) {
          orderConfigForCart.addressId = deliveryAddressId;
        }
        if (deliveryLat && deliveryLng) {
          orderConfigForCart.addressLatitude = deliveryLat;
          orderConfigForCart.addressLongitude = deliveryLng;
        }
      }

      localStorage.setItem('eatzy-cart', JSON.stringify({
        restaurantId: cartRestaurantId,
        items: cartItems,
        orderConfig: orderConfigForCart,
        timestamp: Date.now(),
      }));

      console.log('[Reorder] Saved', cartItems.length, 'items to localStorage cart for', cartRestaurantId);

      toast({
        title: 'Items ready for checkout',
        description: `${cartItems.length} item(s) added successfully`
      });

      onSuccess?.({
        eatzyClientId: resolvedEatzyClientId,
        restaurantId: preview.restaurantId || restaurantId,
        serviceType: preview.currentServiceType || serviceType,
        scheduledTime: scheduledTime || undefined,
        action,
      });
      onClose();
    } catch (error: any) {
      toast({
        title: 'Failed to prepare items',
        description: error.message || 'Please try again',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
      setProcessingAction(null);
    }
  };

  const toggleItem = (index: number) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedItems(newSelected);
  };

  const getStatusIcon = (result: ReorderMatchResult) => {
    switch (result.status) {
      case 'matched':
        return <Check className="w-4 h-4 text-green-600" />;
      case 'price_changed':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'modifiers_changed':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'unavailable':
        return <X className="w-4 h-4 text-red-500" />;
    }
  };

  const getStatusBadge = (result: ReorderMatchResult) => {
    switch (result.status) {
      case 'matched':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Ready</Badge>;
      case 'price_changed':
        return (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
            {result.priceDifference && result.priceDifference > 0 ? '+' : ''}
            ${result.priceDifference?.toFixed(2)}
          </Badge>
        );
      case 'modifiers_changed':
        return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Options changed</Badge>;
      case 'unavailable':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Unavailable</Badge>;
    }
  };

  const formatPrice = (price: number) => `$${price.toFixed(2)}`;

  const selectedTotal = preview?.results.reduce((sum, r, index) => {
    if (!selectedItems.has(index) || !r.matchedMenuItem) return sum;
    const qty = itemQuantities[index] ?? r.originalItem.quantity;
    const basePrice = r.matchedMenuItem.price;
    const modCost = (r.validModifiers || []).reduce((ms, m) => ms + (m.priceAdjustment || 0), 0);
    return sum + (basePrice + modCost) * qty;
  }, 0) || 0;

  const formatScheduledTimeLabel = () => {
    if (!scheduledTime) return '';
    const date = new Date(scheduledTime.startTime * 1000);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md max-h-[85svh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <RefreshCw className="w-5 h-5 text-primary" />
            Order Again
            {serviceType === 'delivery' ? (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800 font-medium">
                <Truck className="w-3 h-3 mr-1" />
                Delivery
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800 font-medium">
                <Package className="w-3 h-3 mr-1" />
                Takeout
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {isLoading || isFetching ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary mb-3" />
            <p className="text-sm text-muted-foreground">
              {scheduledTime ? 'Checking scheduled availability...' : 'Checking item availability...'}
            </p>
          </div>
        ) : showScheduler && closedInfo ? (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-400 p-3 rounded-md">
              <Clock className="w-4 h-4 flex-shrink-0" />
              <span>
                {closedInfo.restaurantName || 'This restaurant'} is currently closed. Schedule your order for a future time.
              </span>
            </div>

            <DateTimeSelector
              onTimeSelect={handleTimeSelect}
              isRestaurantOpen={false}
              hoursJson={closedInfo.hoursJson}
              timezone={closedInfo.timezone}
              serviceType={closedInfo.serviceType as 'delivery' | 'takeout'}
            />

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleScheduleConfirm}
                disabled={!scheduledTime}
              >
                <CalendarClock className="w-4 h-4 mr-2" />
                Check Availability
              </Button>
            </DialogFooter>
          </div>
        ) : previewError && !showScheduler ? (
          <div className="flex flex-col items-center justify-center py-12">
            {closedInfo ? (
              <>
                <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <p className="font-medium text-sm mb-1">No availability at selected time</p>
                <p className="text-xs text-muted-foreground mb-4 text-center px-4">
                  The restaurant doesn't have items available for the selected time. Try picking a different time.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={onClose}>
                    Close
                  </Button>
                  <Button onClick={handleBackToScheduler}>
                    <Clock className="w-4 h-4 mr-2" />
                    Pick Another Time
                  </Button>
                </div>
              </>
            ) : (previewError as Error).message?.includes('unavailable') ? (
              <>
                <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <p className="font-medium text-sm mb-1">Restaurant is currently closed</p>
                <p className="text-xs text-muted-foreground mb-4 text-center px-4">
                  This restaurant isn't accepting orders right now. Try again later during their business hours.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={onClose}>
                    Close
                  </Button>
                  <Button onClick={() => refetch()}>
                    Try Again
                  </Button>
                </div>
              </>
            ) : (
              <>
                <AlertTriangle className="w-8 h-8 text-destructive mb-3" />
                <p className="text-sm text-muted-foreground mb-4 text-center px-4">
                  {(previewError as Error).message || 'Unable to load order items'}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={onClose}>
                    Close
                  </Button>
                  <Button onClick={() => refetch()}>
                    Try Again
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : preview ? (
          <>
            {scheduledTime && (
              <div className="flex items-center gap-2 text-sm bg-muted/50 p-2 rounded-md mb-2">
                <CalendarClock className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-muted-foreground">
                  Scheduled for <span className="font-medium text-foreground">{formatScheduledTimeLabel()}</span>
                </span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="ml-auto h-auto py-1 px-2 text-xs"
                  onClick={handleBackToScheduler}
                >
                  Change
                </Button>
              </div>
            )}

            <div className="text-sm text-muted-foreground mb-3">
              {preview.summary.matchedItems} of {preview.summary.totalItems} items available
              {preview.summary.priceChangedItems > 0 && (
                <span className="text-amber-600 ml-1">
                  ({preview.summary.priceChangedItems} with price changes)
                </span>
              )}
            </div>

            <div className={`flex-1 overflow-y-auto space-y-2 pr-1 ${showTimedScheduler ? 'hidden' : ''}`}>
              {preview.results.map((result, index) => {
                const isUnavailable = result.status === 'unavailable';
                const isSelected = selectedItems.has(index);

                return (
                  <Card
                    key={index}
                    className={`p-3 transition-opacity ${isUnavailable ? 'opacity-50' : ''} ${
                      isSelected && !isUnavailable ? 'ring-1 ring-primary/30' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {!isUnavailable && (
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleItem(index)}
                          className="mt-1"
                        />
                      )}
                      {isUnavailable && (
                        <div className="w-4 mt-1">{getStatusIcon(result)}</div>
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-sm truncate">
                            {result.originalItem.itemName}
                          </span>
                          {getStatusBadge(result)}
                        </div>

                        {result.originalItem.modifiers && result.originalItem.modifiers.length > 0 && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {(result.validModifiers && result.validModifiers.length > 0
                              ? result.validModifiers.map(m =>
                                  m.priceAdjustment && m.priceAdjustment > 0
                                    ? `${m.answerName} +$${m.priceAdjustment.toFixed(2)}`
                                    : (m.answerName || '')
                                )
                              : result.originalItem.modifiers.map(m => m.answerName)
                            ).join(' · ')}
                          </div>
                        )}

                        {result.unavailableModifiers && result.unavailableModifiers.length > 0 && (
                          <div className="text-xs text-amber-600 mt-1">
                            Not available: {result.unavailableModifiers.join(', ')}
                          </div>
                        )}

                        {result.status === 'unavailable' && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                            <Clock className="w-3 h-3 flex-shrink-0" />
                            <span>{result.matchReason || 'Not available at this time'}</span>
                          </div>
                        )}
                        {result.status !== 'matched' && result.status !== 'unavailable' && result.matchReason && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {result.matchReason}
                          </div>
                        )}

                        <div className="flex items-center justify-between mt-2">
                          {!isUnavailable ? (
                            <div className="flex items-center gap-1">
                              <Button
                                size="icon"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setItemQuantities(prev => ({
                                    ...prev,
                                    [index]: Math.max(1, (prev[index] ?? result.originalItem.quantity) - 1),
                                  }));
                                }}
                                disabled={!isSelected}
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <span className="text-sm font-medium min-w-[1.5rem] text-center tabular-nums">
                                {itemQuantities[index] ?? result.originalItem.quantity}
                              </span>
                              <Button
                                size="icon"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setItemQuantities(prev => ({
                                    ...prev,
                                    [index]: Math.min(99, (prev[index] ?? result.originalItem.quantity) + 1),
                                  }));
                                }}
                                disabled={!isSelected}
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <span />
                          )}
                          {result.matchedMenuItem && (
                            <span className="text-sm font-medium">
                              {formatPrice(
                                (result.matchedMenuItem.price +
                                  (result.validModifiers || []).reduce((s, m) => s + (m.priceAdjustment || 0), 0)
                                ) * (itemQuantities[index] ?? result.originalItem.quantity)
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {!showTimedScheduler && preview.summary.unavailableItems > 0 && !isClosedError && (
              <div className="flex items-start gap-2 text-sm bg-muted/40 rounded-md p-3 mt-2">
                <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-muted-foreground">Some items may only be available during certain hours.</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 mt-1 text-xs underline"
                    onClick={() => setShowTimedScheduler(true)}
                  >
                    Schedule order for a specific time
                  </Button>
                </div>
              </div>
            )}

            {showTimedScheduler && (
              <div className="flex flex-col gap-4 pt-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/40 p-3 rounded-md">
                  <Clock className="w-4 h-4 flex-shrink-0" />
                  <span>Pick a time to check if these items are available during that period.</span>
                </div>
                <DateTimeSelector
                  onTimeSelect={handleTimeSelect}
                  isRestaurantOpen={true}
                  hoursJson={null}
                  serviceType={serviceType}
                />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setShowTimedScheduler(false); setScheduledTime(null); }}>
                    Back
                  </Button>
                  <Button
                    onClick={() => { handleScheduleConfirm(); setShowTimedScheduler(false); }}
                    disabled={!scheduledTime}
                  >
                    <CalendarClock className="w-4 h-4 mr-2" />
                    Check Availability
                  </Button>
                </div>
              </div>
            )}

            {!showTimedScheduler && (
              <div className="border-t pt-3 mt-3">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-muted-foreground">
                    {selectedItems.size} item(s) selected
                  </span>
                  <span className="font-semibold">
                    {formatPrice(selectedTotal)}
                  </span>
                </div>
              </div>
            )}
          </>
        ) : null}

        {!showScheduler && !showTimedScheduler && (
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-2">
            <Button variant="outline" onClick={onClose} disabled={isProcessing} className="sm:mr-auto">
              Cancel
            </Button>
            <Button
              onClick={() => handleAddToCart('checkout')}
              disabled={!preview || selectedItems.size === 0 || isProcessing}
            >
              {processingAction === 'checkout' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Proceed to Checkout
                </>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
